import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Play, TrendingUp, TrendingDown, Minus, Target, CheckCircle, BarChart2, Lightbulb, ChevronDown, ChevronRight } from 'lucide-react';
import { ModuleData, ModuleProgress } from '../../types';
import { Button } from '../ui/Button';
import { ProgressRing, ProgressBar } from '../ui/Progress';
import * as Icons from 'lucide-react';
import { useMediaQuery } from 'react-responsive';
import { GroupId } from '../../config/systemConfig';
import { useAppStore } from '../../hooks/useAppStore';
import { computeLevelProgress } from '../../utils/adaptiveEngine';
import { formatSkill, formatSubSkill } from '../../utils/formatters';
import { getModuleDisplayInfo } from '../../config/moduleContent';
import { computeSkillMasteries, SkillMastery } from '../../utils/skillAggregation';
interface ModulePanelProps {
  isOpen: boolean;
  onClose: () => void;
  module: ModuleData | null;
  progress: ModuleProgress | null;
  onStartPractice: (moduleId: string) => void;
  onViewExplanation: (moduleId: string) => void;
  selectedGroup: GroupId;
}
export function ModulePanelRedesigned({
  isOpen,
  onClose,
  module,
  progress,
  onStartPractice,
  onViewExplanation,
  selectedGroup
}: ModulePanelProps) {
  const isMobile = useMediaQuery({
    maxWidth: 768
  });
  const {
    state,
    getStartingLevel
  } = useAppStore();
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  // Get dynamic content
  const displayInfo = module ? getModuleDisplayInfo(module.id, selectedGroup) : null;
  const title = displayInfo?.title || module?.title;
  const description = displayInfo?.description || module?.description;
  // Compute skill-level aggregated masteries
  const skillMasteries = useMemo(() => {
    if (!module) return [];
    return computeSkillMasteries(module.id, selectedGroup, state.questionStates);
  }, [module, selectedGroup, state.questionStates]);
  if (!module || !progress) return null;
  const IconComponent = (Icons as any)[module.icon] || Icons.HelpCircle;
  const bgColorMap: Record<string, string> = {
    'text-blue-500': 'bg-blue-50',
    'text-indigo-500': 'bg-indigo-50',
    'text-emerald-500': 'bg-emerald-50',
    'text-rose-500': 'bg-rose-50',
    'text-amber-500': 'bg-amber-50',
    'text-purple-500': 'bg-purple-50',
    'text-teal-500': 'bg-teal-50',
    'text-cyan-500': 'bg-cyan-50'
  };
  const iconBgColor = bgColorMap[module.color] || 'bg-slate-50';
  // Compute Level Progress for "Current Level" display
  const startingLevel = getStartingLevel(module.id, selectedGroup);
  const levelProgressInfo = computeLevelProgress(module.id, selectedGroup, state.questionStates, startingLevel);
  const currentLevel = levelProgressInfo.levelDetails.find(l => l.isCurrent) || levelProgressInfo.levelDetails[0];
  // Sort skills: Struggling first, then by lowest mastery
  const sortedSkills = [...skillMasteries].sort((a, b) => {
    if (a.trend === 'struggling' && b.trend !== 'struggling') return -1;
    if (b.trend === 'struggling' && a.trend !== 'struggling') return 1;
    return a.masteryScore - b.masteryScore;
  });
  // Find struggling skill for Smart Tip
  const strugglingSkill = sortedSkills.find(s => s.trend === 'struggling' || s.masteryScore < 35);
  return <AnimatePresence data-id="element-1085">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" data-id="element-1086" />

          {/* Panel */}
          <motion.div initial={isMobile ? {
        y: '100%'
      } : {
        x: '100%'
      }} animate={isMobile ? {
        y: 0
      } : {
        x: 0
      }} exit={isMobile ? {
        y: '100%'
      } : {
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className={`
              fixed z-50 bg-white shadow-2xl flex flex-col
              ${isMobile ? 'inset-x-0 bottom-0 h-[85vh] rounded-t-3xl' : 'inset-y-0 right-0 w-full max-w-xl'}
            `} data-id="element-1087">
            {/* Mobile Drag Handle */}
            {isMobile && <div className="w-full flex justify-center pt-3 pb-1" onClick={onClose} data-id="element-1088">
                <div className="w-12 h-1.5 bg-slate-200 rounded-full" data-id="element-1089" />
              </div>}

            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50" data-id="element-1090">
              <div className="flex items-center gap-4" data-id="element-1091">
                <div className={`p-3 rounded-xl ${iconBgColor}`} data-id="element-1092">
                  <IconComponent className={`w-8 h-8 ${module.color}`} data-id="element-1093" />
                </div>
                <div data-id="element-1094">
                  <div className="flex items-center gap-2" data-id="element-1095">
                    <h2 className="text-2xl font-bold text-slate-900" data-id="element-1096">
                      {title}
                    </h2>
                  </div>
                  <p className="text-slate-500 text-sm line-clamp-1" data-id="element-1097">
                    {description}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-1098">
                <X className="w-6 h-6" data-id="element-1099" />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 flex flex-col" data-id="element-1100">
              <div className="flex flex-col h-full space-y-8" data-id="element-1101">
                {/* 1. Quick Stats Row */}
                <div className="grid grid-cols-3 gap-4" data-id="element-1102">
                  {/* Current Level */}
                  <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 flex flex-col items-center justify-center text-center" data-id="element-1103">
                    <div className="mb-2 bg-white p-2 rounded-full shadow-sm" data-id="element-1104">
                      <Target className="w-5 h-5 text-indigo-600" data-id="element-1105" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-1106">
                      {currentLevel?.label || '-'}
                    </div>
                    <div className="text-xs text-slate-500 font-medium line-clamp-1 px-1" data-id="element-1107">
                      {currentLevel?.title || 'Niveau'}
                    </div>
                  </div>

                  {/* Mastery */}
                  <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 flex flex-col items-center justify-center text-center" data-id="element-1108">
                    <div className="mb-2" data-id="element-1109">
                      <ProgressRing progress={progress.masteryPercentage} size={40} strokeWidth={4} showLabel={false} color={progress.masteryPercentage >= 95 ? 'text-emerald-500' : 'text-indigo-600'} data-id="element-1110" />
                    </div>
                    <div className="text-lg font-bold text-slate-900" data-id="element-1111">
                      {Math.round(progress.masteryPercentage)}%
                    </div>
                    <div className="text-xs text-slate-500 font-medium" data-id="element-1112">
                      Beheersing
                    </div>
                  </div>

                  {/* Questions Answered */}
                  <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 flex flex-col items-center justify-center text-center" data-id="element-1113">
                    <div className="mb-2 bg-white p-2 rounded-full shadow-sm" data-id="element-1114">
                      <CheckCircle className="w-5 h-5 text-emerald-600" data-id="element-1115" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-1116">
                      {progress.questionsAnswered.length}
                    </div>
                    <div className="text-xs text-slate-500 font-medium" data-id="element-1117">
                      Geoefend
                    </div>
                  </div>
                </div>

                {/* 2. Skill Mastery Section (aggregated from subSkills) */}
                <div data-id="element-1118">
                  <div className="flex items-center gap-2 mb-4" data-id="element-1119">
                    <BarChart2 className="w-5 h-5 text-slate-400" data-id="element-1120" />
                    <h3 className="font-bold text-slate-900" data-id="element-1121">
                      Jouw Vaardigheden
                    </h3>
                  </div>

                  {sortedSkills.length > 0 ? <div className="space-y-3" data-id="element-1122">
                      {sortedSkills.map(skill => {
                  const isMastered = skill.masteryScore >= 85;
                  const isGood = skill.masteryScore >= 50;
                  const isExpanded = expandedSkill === skill.skill;
                  const hasMultipleSubSkills = skill.subSkills.length > 1;
                  let colorClass = 'bg-amber-500';
                  let bgClass = 'bg-amber-50 border-amber-100';
                  let textClass = 'text-amber-700';
                  if (isMastered) {
                    colorClass = 'bg-emerald-500';
                    bgClass = 'bg-emerald-50 border-emerald-100';
                    textClass = 'text-emerald-700';
                  } else if (isGood) {
                    colorClass = 'bg-indigo-500';
                    bgClass = 'bg-indigo-50 border-indigo-100';
                    textClass = 'text-indigo-700';
                  }
                  return <div key={skill.skill} className={`rounded-xl border ${bgClass} overflow-hidden`} data-id="element-1123">
                            {/* Skill Row (clickable if has multiple subSkills) */}
                            <div className={`p-3 flex items-center justify-between gap-4 ${hasMultipleSubSkills ? 'cursor-pointer' : ''}`} onClick={() => {
                      if (hasMultipleSubSkills) {
                        setExpandedSkill(isExpanded ? null : skill.skill);
                      }
                    }} data-id="element-1124">
                              <div className="flex-1" data-id="element-1125">
                                <div className="flex items-center justify-between mb-1.5" data-id="element-1126">
                                  <div className="flex items-center gap-2" data-id="element-1127">
                                    {hasMultipleSubSkills && (isExpanded ? <ChevronDown className={`w-3.5 h-3.5 ${textClass}`} data-id="element-1128" /> : <ChevronRight className={`w-3.5 h-3.5 ${textClass}`} data-id="element-1129" />)}
                                    <span className={`font-medium text-sm capitalize ${textClass}`} data-id="element-1130">
                                      {formatSkill(skill.skill)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-1131">
                                    {skill.trend === 'improving' && <TrendingUp className="w-3 h-3 text-emerald-500" data-id="element-1132" />}
                                    {skill.trend === 'struggling' && <TrendingDown className="w-3 h-3 text-amber-500" data-id="element-1133" />}
                                    {skill.trend === 'stable' && <Minus className="w-3 h-3 text-slate-400" data-id="element-1134" />}
                                    <span className={`text-xs font-bold ${textClass}`} data-id="element-1135">
                                      {skill.masteryScore}%
                                    </span>
                                  </div>
                                </div>
                                <ProgressBar value={skill.masteryScore} height={4} color={colorClass} className="bg-white/50" data-id="element-1136" />
                              </div>
                            </div>

                            {/* SubSkill Drill-Down (expandable) */}
                            <AnimatePresence data-id="element-1137">
                              {isExpanded && hasMultipleSubSkills && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.2
                      }} className="overflow-hidden" data-id="element-1138">
                                  <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-white/30" data-id="element-1139">
                                    {skill.subSkills.sort((a, b) => a.masteryScore - b.masteryScore).map(sub => <div key={sub.subSkill} className="flex items-center justify-between bg-white/60 rounded-lg px-2.5 py-1.5" data-id="element-1140">
                                          <span className="text-xs text-slate-600 capitalize" data-id="element-1141">
                                            {formatSubSkill(sub.subSkill)}
                                          </span>
                                          <span className="text-xs font-bold text-slate-500" data-id="element-1142">
                                            {sub.masteryScore}%
                                          </span>
                                        </div>)}
                                  </div>
                                </motion.div>}
                            </AnimatePresence>
                          </div>;
                })}
                    </div> : <div className="text-center py-8 bg-slate-50 rounded-2xl border border-slate-100 border-dashed" data-id="element-1143">
                      <p className="text-slate-500 text-sm" data-id="element-1144">
                        Start met oefenen om je vaardigheden te zien!
                      </p>
                    </div>}
                </div>

                {/* 3. Smart Tip (now uses skill name) */}
                {strugglingSkill && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-id="element-1145">
                    <div className="bg-amber-100 p-2 rounded-lg h-fit" data-id="element-1146">
                      <Lightbulb className="w-5 h-5 text-amber-600" data-id="element-1147" />
                    </div>
                    <div data-id="element-1148">
                      <h4 className="font-bold text-amber-800 text-sm mb-1" data-id="element-1149">
                        Focus Tip
                      </h4>
                      <p className="text-xs text-amber-700 leading-relaxed" data-id="element-1150">
                        <span className="font-semibold capitalize" data-id="element-1151">
                          {formatSkill(strugglingSkill.skill)}
                        </span>{' '}
                        kan nog wat extra aandacht gebruiken. Het oefensysteem
                        past zich automatisch aan om je hierbij te helpen!
                      </p>
                    </div>
                  </div>}

                {/* Sticky Bottom Actions */}
                <div className="mt-auto space-y-3 pt-6 border-t border-slate-100" data-id="element-1152">
                  <Button onClick={() => onStartPractice(module.id)} size="lg" fullWidth className="shadow-lg shadow-indigo-200 h-14 text-lg" data-id="element-1153">
                    <Play className="w-5 h-5 mr-2 fill-current" data-id="element-1154" />
                    Start Oefensessie
                  </Button>

                  <Button onClick={() => onViewExplanation(module.id)} variant="ghost" size="md" fullWidth className="text-slate-400 hover:text-slate-600" data-id="element-1155">
                    <BookOpen className="w-4 h-4 mr-2" data-id="element-1156" />
                    Bekijk Uitleg
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}